import * as Sentry from "@sentry/react-native";
import dayjs from "dayjs";

/*
  1. Is there a standard library to use (like zod)
  2. Should we check the validity of the date string in the response via Zod?
  3. How to separate string parsing (via UX) from utils?
    
*/

/**
 * Converts a Date object to a string in "yyyy-mm-dd" format.
 * @param date The Date object to convert
 * @returns A string in "yyyy-mm-dd" format
 */
export function dateToString(date: Date): string {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Converts a string in "yyyy-mm-dd" format to a Date object.
 * @param dateString The date string to convert
 * @returns A Date object
 * @throws Error if the dateString is not in the correct format
 */
export function stringToDate(dateString: string): Date {
  const regex = /^\d{4}-\d{2}-\d{2}$/;
  if (!regex.test(dateString)) {
    throw new Error("Invalid date string format. Use yyyy-mm-dd");
  }
  const parts = dateString.split("-").map(Number);
  if (parts.length !== 3 || parts.some((n) => n === undefined)) {
    throw new Error("Invalid date string format. Use yyyy-mm-dd");
  }
  const [year, month, day] = parts;
  if (year === undefined || month === undefined || day === undefined) {
    throw new Error("Invalid date components");
  }
  return new Date(year, month - 1, day);
}

/**
 * Validates if a string is in the correct "yyyy-mm-dd" format.
 * @param dateString The date string to validate
 * @returns True if the string is in the correct format, false otherwise
 */
export function isValidDateString(dateString: string): boolean {
  const regex = /^\d{4}-\d{2}-\d{2}$/;
  if (!regex.test(dateString)) {
    return false;
  }
  const [yearStr, monthStr, dayStr] = dateString.split("-");
  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);

  if (isNaN(year) || isNaN(month) || isNaN(day)) {
    return false;
  }

  const date = new Date(year, month - 1, day);
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
}

export function calcIsUserOnline(lastActivityTime: number): boolean {
  const currentTime = new Date().getTime() / 1000;
  return currentTime - lastActivityTime <= 15 * 60;
}

/**
 * Formats a datetime string to a readable time format (e.g., "6:30 PM") in its specified timezone.
 *
 * @param {string} dateTimeString - The date-time string to format (ISO format recommended).
 * @returns {string} The formatted time string. Returns "N/A" if the input is invalid.
 *
 * @example
 * const time = getFormattedTime("2023-10-04T18:30:00-07:00");
 * console.log(time); // Output: "6:30 PM"
 */
export function getFormattedTime(dateTimeString: string): string {
  try {
    // Parse the datetime string with dayjs
    const dt = dayjs(dateTimeString);

    // Check if the datetime is valid
    if (!dt.isValid()) {
      throw new Error(
        `Invalid dateTimeString in getFormattedTime() - "${dateTimeString}"`,
      );
    }

    // Format the time in 12-hour format with AM/PM
    return dt.format("h:mm A");
  } catch (error) {
    Sentry.captureException(error);
    return "N/A";
  }
}

// Parse all dates from the API as UTC.
export function parseAsUTC(dateString: string): Date {
  // Add a 'Z' to force UTC interpretation if no timezone is present
  const hasTimezone =
    dateString.includes("Z") || /[+-]\d\d:\d\d$/.test(dateString);

  if (!hasTimezone) {
    dateString += "Z";
  }

  return new Date(dateString);
}

export function utcNow(): Date {
  return new Date(Date.now());
}
